import {
  buyersIn,
  exteriorDoors,
  fmtMult,
  HUB,
  linksOf,
  offlineWorkOf,
  PLAINS_SIGNS,
  plainsExitWarps,
  safeZonesIn,
  smallDungeonState,
  spotsIn,
  ZONE_LIST,
  ZONES,
  zoneEnabled,
  type GameMap,
  type World,
  type ZoneId,
  worldBossOf,
  warpOptions,
} from '@nine/shared';
import { goButton, type Send } from './goButton';
import './worldmap.css';
import { $, closeButton, h } from './dom';
import { SPOT_COLOR } from './trainingSpot';

/** Pixels per tile on the zone map: 8 on small maps, less so the 256 plains stays about 900 px wide. */
const pxFor = (m: GameMap): number => Math.max(2, Math.min(8, 900 / Math.max(m.w, m.h)));
const UI = "'Mitr', system-ui, sans-serif";

/**
 * The big map (M): this zone from above — monsters as red dots, warp circles with where they lead,
 * people and the player — and the world map of every zone and the warps between them.
 */
export class MapView {
  private readonly root: HTMLElement;
  private readonly canvas = h('canvas', { className: 'bigmap-canvas' });
  private readonly tabs: HTMLButtonElement[];
  private view: 'zone' | 'world' = 'zone';
  private readonly switchBtn = h('button', { type: 'button', class: 'bigmap-switch' }, 'แผนที่ใหญ่');
  private t = 0;
  private readonly goList = h('div', { class: 'bigmap-go' });
  private goKey = '';

  constructor(
    private readonly world: World,
    /** The minimap's 2px-per-tile ground image. */
    private readonly ground: HTMLCanvasElement,
    private readonly send?: Send,
  ) {
    const close = closeButton(() => this.close());
    this.tabs = (
      [
        ['zone', 'แผนที่นี้'],
        ['world', 'แผนที่โลก'],
      ] as const
    ).map(([v, label]) => {
      const b = h('button', { type: 'button', role: 'tab' }, label);
      b.addEventListener('click', () => this.show(v));
      return b;
    });
    this.root = h(
      'div',
      { id: 'bigmap', class: 'panel bigmap-card', hidden: true, role: 'dialog', ariaLabel: 'แผนที่' },
      h(
        'div',
        { class: 'panel-top' },
        h('div', { class: 'bigmap-tabs', role: 'tablist' }, ...this.tabs),
        this.switchBtn,
        close,
      ),
      h('div', { class: 'bigmap-body' }, this.canvas),
      this.goList,
      h(
        'div',
        { class: 'bigmap-legend' },
        h('span', { class: 'lg-me' }, 'เจ้า'),
        h('span', { class: 'lg-mon' }, 'มอนสเตอร์'),
        h('span', { class: 'lg-boss' }, 'บอส'),
        h('span', { class: 'lg-npc' }, 'NPC'),
        h('span', { class: 'lg-warp' }, 'ทางออก/วงวาร์ป'),
        h('span', { class: 'lg-spot' }, 'ที่ฝึกวิชา'),
      ),
    );
    this.switchBtn.addEventListener('click', () => this.show(this.view === 'zone' ? 'world' : 'zone'));
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());
    $('#panel').after(this.root);
    this.show('zone');
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.root.hidden = false;
    this.t = 0;
    this.draw();
  }

  close(): void {
    this.root.hidden = true;
  }

  private show(v: 'zone' | 'world'): void {
    this.view = v;
    this.switchBtn.textContent = v === 'zone' ? 'แผนที่ใหญ่' : 'กลับแผนที่นี้';
    for (const [k, b] of this.tabs.entries()) b.ariaSelected = String((k === 0) === (v === 'zone'));
    this.draw();
  }

  /** Redraws a few times a second while open, so the dots move. */
  sync(dt: number): void {
    if (!this.isOpen || (this.t -= dt) > 0) return;
    this.t = 0.15;
    this.draw();
  }

  /** J7: "ไป" buttons for the camps, buyers, exits and the keeper of this zone. Rebuilt only when the set changes. */
  private drawGo(): void {
    const w = this.world;
    const m = w.map;
    type T = Parameters<typeof goButton>[1];
    const items: [string, T][] = [];
    if (this.view === 'zone' && !w.interiors.inside) {
      for (const c of smallDungeonState(w)) items.push([`ค่าย ${c.name}`, { kind: 'point', x: c.x, y: c.y }]);
      for (const b of buyersIn(m.zone)) {
        const e = w.entities.get(offlineWorkOf(w).buyerList().find((x) => x.buyer.id === b.id)?.id ?? -1);
        if (e) items.push(['ตลาดมืด', { kind: 'npc', id: e.id }]);
      }
      for (const p of m.portals) items.push([`ทางออก → ${ZONES[p.to]?.name ?? p.to}`, { kind: 'point', x: p.x, y: p.y }]);
      for (const e of w.entities.values())
        if (e.kind === 'npc' && e.title === 'ผู้ดูแลกิจกรรม') items.push([e.name, { kind: 'npc', id: e.id }]);
    }
    // K31: "วาร์ป" buttons for the discovered stations (paid; cost shown).
    const warps = warpOptions(w.progress, m.zone);
    const key = items.map(([l, t]) => l + JSON.stringify(t)).join('|') + '#' + warps.map((o) => o.station.id + o.cost + !!o.why).join(',');
    if (key === this.goKey) return;
    this.goKey = key;
    this.goList.replaceChildren(
      ...items.map(([l, t]) => h('span', { className: 'bigmap-go-i' }, l, goButton(this.send, t))),
      ...warps.map((o) => {
        const b = h('button', { type: 'button', class: 'go-btn', title: o.why ?? `วาร์ป ${o.cost} ตำลึง`, disabled: !!o.why }, 'วาร์ป') as HTMLButtonElement;
        b.style.cssText = 'margin-left:8px;padding:0 8px;font:inherit;font-size:12px;line-height:18px;border-radius:3px;cursor:pointer;border:1px solid rgba(159,216,255,.75);background:rgba(10,20,40,.55);color:#cfeaff';
        b.addEventListener('pointerdown', (e) => e.stopPropagation());
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          this.send?.({ t: 'fastTravel', to: o.station.id });
        });
        return h('span', { className: 'bigmap-go-i' }, `${o.station.name} · ${o.cost} ตำลึง`, b);
      }),
    );
  }

  private draw(): void {
    this.drawGo();
    if (this.view === 'zone') this.drawZone();
    else this.drawWorld();
  }

  private drawZone(): void {
    const w = this.world;
    const m: GameMap = w.map;
    const c = this.canvas;
    const PX = pxFor(m);
    c.width = m.w * PX;
    c.height = m.h * PX;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.drawImage(this.ground, 0, 0, c.width, c.height);
    const dot = (x: number, y: number, r: number, fill: string) => {
      g.fillStyle = fill;
      g.beginPath();
      g.arc(x * PX, y * PX, r, 0, Math.PI * 2);
      g.fill();
    };
    const label = (text: string, x: number, y: number, fill: string, align: CanvasTextAlign = 'center') => {
      g.font = `500 13px ${UI}`;
      g.textAlign = align;
      g.lineWidth = 4;
      g.strokeStyle = 'rgba(10,8,14,.9)';
      g.strokeText(text, x, y);
      g.fillStyle = fill;
      g.fillText(text, x, y);
    };
    // Safe plaza circles (data/safeZones).
    for (const sz of safeZonesIn(m.zone)) {
      if (sz.circle) {
        g.strokeStyle = 'rgba(255,226,140,.9)';
        g.fillStyle = 'rgba(255,226,140,.12)';
        g.lineWidth = 2;
        g.setLineDash([8, 5]);
        g.beginPath();
        g.arc(sz.circle.at[0] * PX, sz.circle.at[1] * PX, sz.circle.r * PX, 0, Math.PI * 2);
        g.fill();
        g.stroke();
        g.setLineDash([]);
        label(sz.name, sz.circle.at[0] * PX, (sz.circle.at[1] - sz.circle.r) * PX + 14, '#ffe6a8');
      } else if (sz.rect) {
        g.strokeStyle = 'rgba(255,226,140,.8)';
        g.lineWidth = 2;
        g.strokeRect(sz.rect[0] * PX, sz.rect[1] * PX, (sz.rect[2] - sz.rect[0]) * PX, (sz.rect[3] - sz.rect[1]) * PX);
      }
    }
    // Doors of buildings you can enter (warehouse, training hall, halls).
    if (!w.interiors.inside)
      for (const d of exteriorDoors(m)) {
        const st = m.structures.find((s) => s.id === d.building);
        g.fillStyle = '#ffb347';
        g.fillRect((d.i + 0.5) * PX - 4, (d.j + 0.5) * PX - 4, 8, 8);
        label(st?.name ?? d.building, (d.i + 0.5) * PX, (d.j + 0.5) * PX - 12, '#ffd9a0');
      }
    // Signposts of the plains.
    if (m.zone === HUB)
      for (const sg of PLAINS_SIGNS) {
        g.fillStyle = '#e9d9b0';
        g.fillRect(sg.at[0] * PX - 3, sg.at[1] * PX - 3, 6, 6);
        g.strokeStyle = '#1c1a17';
        g.lineWidth = 1;
        g.strokeRect(sg.at[0] * PX - 3, sg.at[1] * PX - 3, 6, 6);
      }
    // Warp circles, labelled with where they lead.
    g.font = `500 13px ${UI}`;
    g.textBaseline = 'middle';
    for (const p of m.portals) {
      g.strokeStyle = '#9fd8ff';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(p.x * PX, p.y * PX, 9, 0, Math.PI * 2);
      g.stroke();
      dot(p.x, p.y, 4, '#5aaaff');
      const z = ZONES[p.to];
      const label = `ไป ${z.name}`;
      g.textAlign = p.side === 'w' ? 'left' : p.side === 'e' ? 'right' : 'center';
      const lx = p.x * PX + (p.side === 'w' ? 14 : p.side === 'e' ? -14 : 0);
      const ly = p.y * PX + (p.side === 'n' ? 20 : p.side === 's' ? -20 : 0);
      g.lineWidth = 4;
      g.strokeStyle = 'rgba(10,8,14,.9)';
      g.strokeText(label, lx, ly);
      g.fillStyle = '#cfeaff';
      g.fillText(label, lx, ly);
    }
    // Training spots (data/trainingSpots.ts): a jade ring the size of the place, named with its bonus.
    if (!w.interiors.inside)
      for (const s of spotsIn(m.zone)) {
        g.strokeStyle = SPOT_COLOR;
        g.lineWidth = 2;
        g.beginPath();
        g.arc(s.area.x * PX, s.area.y * PX, s.area.r * PX, 0, Math.PI * 2);
        g.stroke();
        g.fillStyle = 'rgba(111,224,180,.16)';
        g.fill();
        const label = `${s.name} ${fmtMult(s.mult)}`;
        g.textAlign = 'center';
        g.lineWidth = 4;
        g.strokeStyle = 'rgba(10,8,14,.9)';
        g.strokeText(label, s.area.x * PX, (s.area.y + s.area.r) * PX + 12);
        g.fillStyle = '#c8f7e2';
        g.fillText(label, s.area.x * PX, (s.area.y + s.area.r) * PX + 12);
      }
    // Small open camp (H16): a tent icon and name, grey while cleared and waiting to respawn.
    for (const c of smallDungeonState(w)) {
      const on = !c.cleared;
      g.fillStyle = on ? '#e0603c' : '#8a8a8a';
      g.strokeStyle = '#1c1a17';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(c.x * PX, c.y * PX - 9);
      g.lineTo(c.x * PX + 8, c.y * PX + 6);
      g.lineTo(c.x * PX - 8, c.y * PX + 6);
      g.closePath();
      g.stroke();
      g.fill();
      label(c.name, c.x * PX, c.y * PX + 20, on ? '#ffc9a8' : '#a8a8a8');
    }
    // Black-market buyers (H24): a violet diamond with the name of the trade.
    for (const b of buyersIn(m.zone)) {
      const e = w.entities.get(offlineWorkOf(w).buyerList().find((x) => x.buyer.id === b.id)?.id ?? -1);
      if (!e) continue;
      g.fillStyle = '#b06cff';
      g.strokeStyle = '#1c1a17';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(e.x * PX, e.y * PX - 7);
      g.lineTo(e.x * PX + 6, e.y * PX);
      g.lineTo(e.x * PX, e.y * PX + 7);
      g.lineTo(e.x * PX - 6, e.y * PX);
      g.closePath();
      g.stroke();
      g.fill();
      label('ตลาดมืด', e.x * PX, e.y * PX + 18, '#dcc3ff');
    }
    for (const e of w.entities.values()) {
      if (e.dead) continue;
      if (e.kind === 'monster') {
        const boss = e.brain?.defId === 'boss' || e.brain?.defId === 'worldboss';
        if (!boss) dot(e.x, e.y, 14, 'rgba(255,74,58,.10)'); // a pocket reads as a red patch
        dot(e.x, e.y, boss ? 6 : 3, boss ? '#c05bff' : '#ff4a3a');
      } else if (e.kind === 'npc') dot(e.x, e.y, 4, '#74d4ad');
      else if (e.kind === 'ambient' || e.kind === 'rival') dot(e.x, e.y, 3, '#7fb6ff');
    }
    // A live wild master (L9): a gold diamond and its name while it roams.
    const wm = w.sys.wildMasters?.mapMark();
    if (wm) {
      g.fillStyle = '#ffd35a';
      g.beginPath();
      g.moveTo(wm.x * PX, wm.y * PX - 7);
      g.lineTo(wm.x * PX + 6, wm.y * PX);
      g.lineTo(wm.x * PX, wm.y * PX + 7);
      g.lineTo(wm.x * PX - 6, wm.y * PX);
      g.fill();
      label(wm.name, wm.x * PX, wm.y * PX - 11, '#ffd35a');
    }
    // A live world boss (H17): a gold ring on its arena.
    const wb = worldBossOf(w)?.state();
    if (wb && !wb.over) {
      g.strokeStyle = '#ffd35a';
      g.lineWidth = 3;
      g.beginPath();
      g.arc(wb.arena.x * PX, wb.arena.y * PX, Math.max(10, wb.arena.r * PX), 0, Math.PI * 2);
      g.stroke();
      g.textAlign = 'center';
      g.fillStyle = '#ffd35a';
      g.fillText('บอสโลก', wb.arena.x * PX, wb.arena.y * PX - Math.max(10, wb.arena.r * PX) - 4);
    }
    const p = w.player;
    // The player: an arrow along the way they face.
    const ang = Math.atan2(p.dirY, p.dirX) || 0;
    g.save();
    g.translate(p.x * PX, p.y * PX);
    g.rotate(ang);
    g.fillStyle = '#fff3c4';
    g.strokeStyle = '#1c1a17';
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(11, 0);
    g.lineTo(-7, 7);
    g.lineTo(-3, 0);
    g.lineTo(-7, -7);
    g.closePath();
    g.stroke();
    g.fill();
    g.restore();
    g.textAlign = 'left';
    g.font = `600 15px ${UI}`;
    const z = ZONES[m.zone];
    g.lineWidth = 4;
    g.strokeStyle = 'rgba(10,8,14,.9)';
    g.strokeText(`${z.name} · ${z.levels}`, 12, 18);
    g.fillStyle = '#f2e4c4';
    g.fillText(`${z.name} · ${z.levels}`, 12, 18);
  }

  /** Dummy node graph: plains in the middle, towns below, sects around, closed zones greyed along the bottom. */
  private drawWorld(): void {
    const c = this.canvas;
    [c.width, c.height] = [900, 600];
    const g = c.getContext('2d')!;
    g.fillStyle = '#15111a';
    g.fillRect(0, 0, c.width, c.height);
    const here = this.world.map.zone;
    const pos = new Map<string, [number, number]>();
    pos.set(HUB, [450, 250]);
    const towns: ZoneId[] = ['town-b', 'town-c', 'town-d'];
    towns.forEach((t, k) => pos.set(t, [250 + k * 200, 430]));
    const sects = ZONE_LIST.length ? Object.keys(ZONES).filter((id) => id.startsWith('sect-')) : [];
    sects.forEach((s, k) => {
      const a = Math.PI * (1.08 + (k / Math.max(1, sects.length - 1)) * 0.84); // an arc over the plains
      pos.set(s, [450 + Math.cos(a) * 380, 270 + Math.sin(a) * 190]);
    });
    const closed = ZONE_LIST.filter((z) => !zoneEnabled(z.id) && z.id !== 'yanyu');
    closed.forEach((z, k) => pos.set(z.id, [60 + k * (780 / Math.max(1, closed.length)) + 40, 560]));
    // Roads: real ones first (the plains exit warps, every zone's warps), the rest dummy-dashed to the plains.
    const real = new Set<string>();
    const key = (a: string, b: string) => [a, b].sort().join('|');
    for (const w of plainsExitWarps()) real.add(key(HUB, w.to));
    for (const z of ZONE_LIST) for (const l of linksOf(z.id)) real.add(key(z.id, l.to));
    for (const p of this.world.map.portals) real.add(key(here, p.to));
    const line = (a: string, b: string, solid: boolean) => {
      const [pa, pb] = [pos.get(a), pos.get(b)];
      if (!pa || !pb) return;
      g.strokeStyle = solid ? '#5aaaff' : '#5a5560';
      g.lineWidth = solid ? 3 : 2;
      g.setLineDash(solid ? [] : [6, 6]);
      g.beginPath();
      g.moveTo(pa[0], pa[1]);
      g.lineTo(pb[0], pb[1]);
      g.stroke();
    };
    for (const id of [...towns, ...sects]) if (zoneEnabled(id as ZoneId)) line(HUB, id, real.has(key(HUB, id)));
    g.setLineDash([]);
    const [cw, ch] = [150, 56];
    for (const [id, [x, y]] of pos) {
      const z = ZONES[id as ZoneId];
      if (!z) continue;
      const open = zoneEnabled(z.id);
      const me = z.id === here;
      g.fillStyle = !open ? '#1d1a20' : me ? '#4a3a1c' : z.id === HUB ? '#2f2838' : '#241e2a';
      g.fillRect(x - cw / 2, y - ch / 2, cw, ch);
      g.strokeStyle = me ? '#ffd27a' : open ? '#6a7a9a' : '#3a3740';
      g.lineWidth = me ? 4 : 1.5;
      g.strokeRect(x - cw / 2 + 0.5, y - ch / 2 + 0.5, cw - 1, ch - 1);
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = `600 15px ${UI}`;
      g.fillStyle = !open ? '#6a6670' : me ? '#ffe6a8' : '#efe7d6';
      g.fillText(z.name, x, y - 8);
      g.font = `400 12px ${UI}`;
      g.fillStyle = !open ? '#5a5660' : me ? '#ffd27a' : '#a59b89';
      g.fillText(me ? '◆ เจ้าอยู่ที่นี่' : open ? (z.kind === 'school' || z.id.startsWith('sect-') ? 'สำนัก' : z.id === HUB ? 'ทุ่งราบ' : 'เมือง') : 'ปิดอยู่', x, y + 14);
    }
    g.textAlign = 'left';
    g.font = `400 12px ${UI}`;
    g.fillStyle = '#9fd8ff';
    g.fillText('เส้นทึบ = ทางที่มีจริง · เส้นประ = ทางที่ยังไม่ทำ', 16, 24);
  }
}
